import React, { FC, ReactElement, useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Input,
  message,
  Popconfirm,
  Typography,
  Upload,
  Image,
  Progress,
} from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../constants/urlConstants";

const { TabPane } = Tabs;

interface Slider {
  id?: number;
  name: string;
  url: string;
  position?: number;
  perfumeId?: number;
}

interface Brand {
  id?: number;
  name: string;
  image: string;
  position?: number;
}

interface PerfumeHighlight {
  id?: number;
  perfumeId: number;
  position?: number;
}

const ManageHome: FC = (): ReactElement => {
  const [activeTab, setActiveTab] = useState("sliders");
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [highlights, setHighlights] = useState<PerfumeHighlight[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // ✅ Fetch all data
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [slidersRes, brandsRes, featuredRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/v1/home/sliders`),
        axios.get(`${BASE_URL}/api/v1/home/brands`),
        axios.get(`${BASE_URL}/api/v1/home/featured`),
      ]);
      setSliders(slidersRes.data);
      setBrands(brandsRes.data);
      setHighlights(featuredRes.data);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch home data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Upload (controlled version)
  const handleUpload = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);

    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj as File;
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreview(null);
    }
  };

  // ✅ Handle Add (Sliders / Brands / Featured)
  const handleAdd = async () => {
    if (!formData.name && activeTab !== "featured") {
      message.warning("Please enter a name");
      return;
    }

    const form = new FormData();

    try {
      setUploadProgress(0);

      if (activeTab === "sliders") {
        form.append(
          "slider",
          new Blob(
            [
              JSON.stringify({
                name: formData.name,
                position: formData.position,
                perfumeId: formData.perfumeId,
              }),
            ],
            { type: "application/json" }
          )
        );

        if (!selectedFile) {
          message.warning("Please select an image before saving.");
          return;
        }

        form.append("file", selectedFile, selectedFile.name);

        await axios.post(`${BASE_URL}/api/v1/home/sliders`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          },
        });
      } else if (activeTab === "brands") {
        form.append(
          "brand",
          new Blob(
            [
              JSON.stringify({
                name: formData.name,
                position: formData.position,
              }),
            ],
            { type: "application/json" }
          )
        );

        if (!selectedFile) {
          message.warning("Please select an image before saving.");
          return;
        }

        form.append("file", selectedFile, selectedFile.name);

        await axios.post(`${BASE_URL}/api/v1/home/brands`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            }
          },
        });
      } else if (activeTab === "featured") {
        await axios.post(`${BASE_URL}/api/v1/home/featured`, formData);
      }

      message.success("Added successfully!");
      setModalVisible(false);
      setFormData({});
      setSelectedFile(null);
      setPreview(null);
      setFileList([]);
      setUploadProgress(0);
      fetchAll();
    } catch (error) {
      console.error(error);
      message.error("Failed to add item");
      setUploadProgress(0);
    }
  };

  // ✅ Handle Delete
  const handleDelete = async (id: number) => {
    try {
      const endpoint =
        activeTab === "sliders"
          ? "sliders"
          : activeTab === "brands"
          ? "brands"
          : "featured";

      await axios.delete(`${BASE_URL}/api/v1/home/${endpoint}/${id}`);
      message.success("Deleted successfully!");
      fetchAll();
    } catch (error) {
      console.error(error);
      message.error("Failed to delete item");
    }
  };

  // ✅ Render Table
  const renderTable = () => {
    if (activeTab === "sliders") {
      return (
        <Table
          dataSource={sliders}
          rowKey="id"
          loading={loading}
          columns={[
            { title: "ID", dataIndex: "id", width: 80 },
            { title: "Name", dataIndex: "name" },
            {
              title: "Image",
              render: (_, record) => (
                <Image src={record.url} alt={record.name} width={120} height={60} />
              ),
            },
            {
              title: "Action",
              render: (_, record) => (
                <Popconfirm
                  title="Delete this slider?"
                  onConfirm={() => handleDelete(record.id!)}
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      );
    }

    if (activeTab === "brands") {
      return (
        <Table
          dataSource={brands}
          rowKey="id"
          loading={loading}
          columns={[
            { title: "ID", dataIndex: "id", width: 80 },
            { title: "Name", dataIndex: "name" },
            {
              title: "Image",
              render: (_, record) => (
                <Image src={record.image} alt={record.name} width={120} height={60} />
              ),
            },
            {
              title: "Action",
              render: (_, record) => (
                <Popconfirm
                  title="Delete this brand?"
                  onConfirm={() => handleDelete(record.id!)}
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      );
    }

    return (
      <Table
        dataSource={highlights}
        rowKey="id"
        loading={loading}
        columns={[
          { title: "ID", dataIndex: "id", width: 80 },
          { title: "Perfume ID", dataIndex: "perfumeId" },
          {
            title: "Action",
            render: (_, record) => (
              <Popconfirm
                title="Delete this highlight?"
                onConfirm={() => handleDelete(record.id!)}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />
    );
  };

  return (
    <div style={{ padding: "30px", background: "#f5f6f7", minHeight: "100vh" }}>
      <Card
        title="Manage Home Page"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setModalVisible(true);
              setFormData({});
              setSelectedFile(null);
              setPreview(null);
              setFileList([]);
              setUploadProgress(0);
            }}
          >
            Add New
          </Button>
        }
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          background: "#fff",
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="🏞️ Sliders" key="sliders" />
          <TabPane tab="🏷️ Brands" key="brands" />
          <TabPane tab="💎 Featured Perfumes" key="featured" />
        </Tabs>

        <div style={{ marginTop: 16 }}>{renderTable()}</div>
      </Card>

      {/* Modal */}
      <Modal
        title={`Add New ${
          activeTab === "sliders"
            ? "Slider"
            : activeTab === "brands"
            ? "Brand"
            : "Featured Perfume"
        }`}
        open={modalVisible}
        onOk={handleAdd}
        onCancel={() => {
          setModalVisible(false);
          setSelectedFile(null);
          setPreview(null);
          setFileList([]);
        }}
        okText="Save"
      >
        {/* 🟦 SLIDER */}
        {activeTab === "sliders" && (
          <>
            <Input
              placeholder="Perfume ID"
              type="number"
              value={formData.perfumeId}
              onChange={(e) =>
                setFormData({ ...formData, perfumeId: Number(e.target.value) })
              }
              style={{ marginBottom: 12 }}
            />
            <Input
              placeholder="Slider Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ marginBottom: 12 }}
            />
            <Input
              placeholder="Position"
              type="number"
              value={formData.position}
              onChange={(e) =>
                setFormData({ ...formData, position: Number(e.target.value) })
              }
              style={{ marginBottom: 12 }}
            />

            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={handleUpload}
              showUploadList={{
                showPreviewIcon: false,
                showRemoveIcon: true,
              }}
              onRemove={() => {
                setSelectedFile(null);
                setPreview(null);
                setFileList([]);
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Slider Image</Button>
              <Typography.Text style={{ display: "block", textAlign: "center", marginTop: 10, color: "#777" }}>
                               To fit on Slider, 1600 × 300 size is recommended
                             </Typography.Text>
            </Upload>

            {preview && (
              <Image
                src={preview}
                alt="preview"
                width={180}
                style={{ marginTop: 12, borderRadius: 8 }}
              />
            )}

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Progress percent={uploadProgress} status="active" style={{ marginTop: 12 }} />
            )}
          </>
        )}

        {/* 🟩 BRAND */}
        {activeTab === "brands" && (
          <>
            <Input
              placeholder="Brand Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ marginBottom: 12 }}
            />
            <Input
              placeholder="Position (optional)"
              type="number"
              value={formData.position}
              onChange={(e) =>
                setFormData({ ...formData, position: Number(e.target.value) })
              }
              style={{ marginBottom: 12 }}
            />

            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={handleUpload}
              showUploadList={{
                showPreviewIcon: false,
                showRemoveIcon: true,
              }}
              onRemove={() => {
                setSelectedFile(null);
                setPreview(null);
                setFileList([]);
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Brand Image</Button>

            </Upload>

            {preview && (
              <Image
                src={preview}
                alt="preview"
                width={180}
                style={{ marginTop: 12, borderRadius: 8 }}
              />
            )}

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Progress percent={uploadProgress} status="active" style={{ marginTop: 12 }} />
            )}
          </>
        )}

        {/* 💎 FEATURED PERFUME */}
        {activeTab === "featured" && (
          <>
            <Input
              placeholder="Perfume ID"
              type="number"
              value={formData.perfumeId}
              onChange={(e) =>
                setFormData({ ...formData, perfumeId: Number(e.target.value) })
              }
              style={{ marginBottom: 12 }}
            />
            <Input
              placeholder="Position"
              type="number"
              value={formData.position}
              onChange={(e) =>
                setFormData({ ...formData, position: Number(e.target.value) })
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ManageHome;
